import { CustomerInfo, Query } from '@varo/graphql-schema-types';
import GetProfileInfo from 'services/graphql/queries/GetProfileInfo';
import { useQuery, UseQueryState } from 'urql';

interface UseUserProfileResult {
  /** User profile data from GraphQL - uses generated CustomerInfo type */
  customerInfo: CustomerInfo | null;
  /** User identity ID */
  identityId: string | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if query failed */
  _error: Error | undefined;
  /** Refetch function to manually refresh user data */
  refetch: () => void;
  /** Raw urql state for advanced use cases */
  queryResponse: UseQueryState<Query>;
}

/**
 * Hook to fetch and access user profile information from GraphQL.
 *
 * This hook replaces the old UserInfoProvider pattern and queries GraphQL directly.
 * The urql cache acts as the single source of truth for user data.
 *
 * @param options - Query options
 * @param options.requestPolicy - Urql request policy. Defaults to 'cache-first' for instant reads.
 * @returns CustomerInfo from GQL, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { customerInfo, isLoading, _error, refetch } = useUserProfile();
 *
 * if (isLoading) return <Loading />;
 * if (_error) return <Error />;
 * if (!customerInfo) return <NoData />;
 *
 * return <div>{customerInfo.email?.address}</div>;
 * ```
 */
export function useUserProfile(options?: {
  requestPolicy?:
    | 'cache-first'
    | 'cache-only'
    | 'network-only'
    | 'cache-and-network';
}): UseUserProfileResult {
  const [result, refetch] = useQuery<Query>({
    query: GetProfileInfo,
    requestPolicy: options?.requestPolicy ?? 'cache-first',
  });

  return {
    customerInfo: result.data?.user?.customerInfo ?? null,
    identityId: result.data?.user?.id ?? null,
    isLoading: result.fetching,
    _error: result.error,
    refetch: () => refetch({ requestPolicy: 'network-only' }),
    queryResponse: result,
  };
}
